<template>
  <div class="debug-access-container">
    <h1>Debug - Sistema de Control de Acceso</h1>
    
    <div class="debug-section">
      <h2>Estado de Autenticación</h2>
      <div class="debug-item">
        <strong>Está autenticado:</strong> {{ isAuthenticated }}
      </div>
      <div class="debug-item">
        <strong>Token presente:</strong> {{ hasToken }}
      </div>
      <div class="debug-item">
        <strong>Usuario:</strong> {{ userData ? JSON.stringify(userData, null, 2) : 'No disponible' }}
      </div>
    </div>

    <div class="debug-section">
      <h2>Menú del Usuario</h2>
      <div class="debug-item">
        <strong>Menú cargado:</strong> {{ hasMenu }}
      </div>
      <div class="debug-item">
        <strong>Estructura del menú:</strong>
        <pre>{{ menuStructure }}</pre>
      </div>
    </div>

    <div class="debug-section">
      <h2>Verificación de Rutas</h2>
      <div class="debug-item">
        <strong>Ruta /modulos en menú:</strong> {{ hasModulosRoute }}
      </div>
      <div class="debug-item">
        <strong>Rutas disponibles:</strong>
        <ul>
          <li v-for="route in availableRoutes" :key="route">{{ route }}</li>
        </ul>
      </div>
    </div>

    <div class="debug-section">
      <h2>LocalStorage Completo</h2>
      <div class="debug-item">
        <pre>{{ localStorageData }}</pre>
      </div>
    </div>

    <div class="debug-section">
      <h2>Pruebas de Acceso</h2>
      <div class="debug-item">
        <button @click="testModulosAccess">Probar acceso a /modulos</button>
        <div v-if="accessTestResult" class="test-result">
          {{ accessTestResult }}
        </div>
      </div>
    </div>

    <div class="debug-section">
      <h2>Acciones</h2>
      <button @click="refreshData">Actualizar datos</button>
      <button @click="clearStorage">Limpiar localStorage</button>
      <button @click="goToModulos">Ir a Módulos</button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import authService from '@/services/auth'
import { useAccessControl } from '@/composables/useAccessControl'

export default {
  name: 'DebugAccessView',
  setup() {
    const router = useRouter()
    const { checkMenuPermission, checkAuthentication } = useAccessControl()
    
    const isAuthenticated = ref(false)
    const hasToken = ref(false)
    const userData = ref(null)
    const hasMenu = ref(false)
    const menuStructure = ref('')
    const hasModulosRoute = ref(false)
    const availableRoutes = ref([])
    const localStorageData = ref('')
    const accessTestResult = ref('')

    const loadDebugData = () => {
      // Verificar autenticación
      isAuthenticated.value = authService.isAuthenticated()
      hasToken.value = !!authService.getToken()
      userData.value = authService.getUser()
      
      // Verificar menú
      const menu = authService.getUserMenu()
      hasMenu.value = !!menu
      menuStructure.value = menu ? JSON.stringify(menu, null, 2) : 'No hay menú disponible'
      
      // Verificar ruta específica
      hasModulosRoute.value = checkMenuPermission('/modulos')
      
      // Extraer rutas disponibles del menú
      if (menu && Array.isArray(menu)) {
        const routes = []
        const extractRoutes = (items) => {
          items.forEach(item => {
            if (item.Url) routes.push(item.Url)
            if (item.children && Array.isArray(item.children)) {
              extractRoutes(item.children)
            }
          })
        }
        extractRoutes(menu)
        availableRoutes.value = routes
      }
      
      // Mostrar localStorage completo
      const storage = {}
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        storage[key] = localStorage.getItem(key)
      }
      localStorageData.value = JSON.stringify(storage, null, 2)
    }

    const testModulosAccess = () => {
      const authCheck = checkAuthentication()
      const menuCheck = checkMenuPermission('/modulos')
      
      accessTestResult.value = `
Verificación de autenticación: ${authCheck}
Verificación de permisos de menú: ${menuCheck}
Acceso permitido: ${authCheck && menuCheck}
      `
    }

    const refreshData = () => {
      loadDebugData()
      accessTestResult.value = ''
    }

    const clearStorage = () => {
      if (confirm('¿Estás seguro de que quieres limpiar el localStorage?')) {
        localStorage.clear()
        refreshData()
      }
    }

    const goToModulos = () => {
      router.push('/modulos')
    }

    onMounted(() => {
      loadDebugData()
    })

    return {
      isAuthenticated,
      hasToken,
      userData,
      hasMenu,
      menuStructure,
      hasModulosRoute,
      availableRoutes,
      localStorageData,
      accessTestResult,
      testModulosAccess,
      refreshData,
      clearStorage,
      goToModulos
    }
  }
}
</script>

<style scoped>
.debug-access-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.debug-section {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.debug-section h2 {
  margin-top: 0;
  color: #333;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
}

.debug-item {
  margin-bottom: 15px;
  padding: 10px;
  background-color: white;
  border-radius: 4px;
  border-left: 4px solid #007bff;
}

.debug-item strong {
  color: #555;
}

pre {
  background-color: #f4f4f4;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
}

button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 10px;
  margin-bottom: 10px;
}

button:hover {
  background-color: #0056b3;
}

.test-result {
  margin-top: 10px;
  padding: 10px;
  background-color: #e9ecef;
  border-radius: 4px;
  white-space: pre-line;
}

ul {
  max-height: 200px;
  overflow-y: auto;
}
</style>